import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "./ThemeProvider";
import api from "../services/api";
import { formatApiError } from "../utils/helpers";

const BASE_LABEL = "Fotos para Retoque";

function formatBytes(n) {
  if (n == null || n === 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fileIcon(name, isDir) {
  if (isDir) return "📁";
  const lower = name.toLowerCase();
  if (/\.(jpe?g|jpe|png|webp|gif|heic|heif|avif|tiff?|bmp|raw|dng|cr2|cr3|nef|arw|orf|raf|rw2|psd|svg)$/.test(lower)) return "🖼️";
  if (/\.(mp4|mov|m4v|webm|avi|mkv|wmv|flv|mpeg|mpg|3gp|mts|insv|lrv)$/.test(lower)) return "🎬";
  if (/\.pdf$/.test(lower)) return "📄";
  return "📄";
}

function pathSegments(relativePath) {
  const parts = relativePath ? relativePath.split("/").filter(Boolean) : [];
  const crumbs = [{ label: BASE_LABEL, path: "" }];
  let acc = "";
  for (const p of parts) {
    acc = acc ? `${acc}/${p}` : p;
    crumbs.push({ label: p, path: acc });
  }
  return crumbs;
}

/**
 * Explorador NAS estilo Windows para «Fotos para Retoque».
 * Casillas de selección solo en la raíz (cada captación = carpeta de primer nivel).
 */
export default function MarcoNasExplorer({ onSelectionChange }) {
  const { theme } = useTheme();
  const isDark = theme.name === "dark";
  const c = theme.colors;

  const win = useMemo(() => ({
    chrome: isDark ? "#2d2d30" : "#f3f3f3",
    chromeBorder: isDark ? "#3e3e42" : "#e5e5e5",
    addr: isDark ? "#1e1e1e" : "#ffffff",
    listBg: isDark ? "#1e1e1e" : "#ffffff",
    listHeader: isDark ? "#252526" : "#fafafa",
    rowHover: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    rowSelected: isDark ? "rgba(124,58,237,0.22)" : "rgba(124,58,237,0.12)",
    divider: isDark ? "#3e3e42" : "#ebebeb",
    text: c.textPrimary,
    muted: c.textMuted,
  }), [isDark, c]);

  const [currentPath, setCurrentPath] = useState("");
  const [parentPath, setParentPath] = useState(null);
  const [isRoot, setIsRoot] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([""]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [selectedCaptaciones, setSelectedCaptaciones] = useState(() => new Set());
  const [initializedSelection, setInitializedSelection] = useState(false);
  const [focusedName, setFocusedName] = useState(null);

  const notifySelection = useCallback((set) => {
    onSelectionChange?.(Array.from(set).sort((a, b) => a.localeCompare(b, "es")));
  }, [onSelectionChange]);

  const loadPath = useCallback(async (path, { autoSelectRoot = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const q = path ? `?path=${encodeURIComponent(path)}` : "";
      const res = await api.get(`/multimedia/retoque/nas/browse${q}`);
      const data = res.data;
      if (!data.ok) throw new Error(formatApiError(data, "Error al listar el NAS"));

      setCurrentPath(data.path || "");
      setParentPath(data.parent != null ? data.parent : null);
      setIsRoot(!!data.isRoot);
      setItems(data.items || []);

      if (data.isRoot && autoSelectRoot && !initializedSelection) {
        const dirs = (data.items || []).filter((i) => i.type === "directory").map((i) => i.name);
        const next = new Set(dirs);
        setSelectedCaptaciones(next);
        setInitializedSelection(true);
        notifySelection(next);
      }
    } catch (e) {
      setError(formatApiError(e, "Error de red"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [initializedSelection, notifySelection]);

  useEffect(() => {
    loadPath(currentPath, { autoSelectRoot: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateTo = useCallback((path, pushHistory = true) => {
    setFocusedName(null);
    if (pushHistory) {
      setHistory((prev) => {
        const trimmed = prev.slice(0, historyIdx + 1);
        return [...trimmed, path];
      });
      setHistoryIdx((i) => i + 1);
    }
    setCurrentPath(path);
    loadPath(path);
  }, [historyIdx, loadPath]);

  const goBack = () => {
    if (historyIdx <= 0) return;
    const nextIdx = historyIdx - 1;
    const path = history[nextIdx];
    setHistoryIdx(nextIdx);
    setCurrentPath(path);
    setFocusedName(null);
    loadPath(path);
  };

  const goUp = () => {
    if (parentPath == null && currentPath === "") return;
    navigateTo(parentPath ?? "");
  };

  const toggleCaptacion = (name) => {
    setSelectedCaptaciones((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      notifySelection(next);
      return next;
    });
  };

  const selectAllCaptaciones = () => {
    const dirs = items.filter((i) => i.type === "directory").map((i) => i.name);
    const next = new Set(dirs);
    setSelectedCaptaciones(next);
    notifySelection(next);
  };

  const deselectAllCaptaciones = () => {
    setSelectedCaptaciones(new Set());
    notifySelection(new Set());
  };

  const crumbs = pathSegments(currentPath);
  const canGoBack = historyIdx > 0;
  const canGoUp = currentPath !== "";
  const rootDirs = items.filter((i) => i.type === "directory");
  const allRootSelected = isRoot && rootDirs.length > 0 && rootDirs.every((d) => selectedCaptaciones.has(d.name));
  const someRootSelected = isRoot && rootDirs.some((d) => selectedCaptaciones.has(d.name));

  const btnStyle = (disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 28,
    borderRadius: 4,
    border: `1px solid ${win.chromeBorder}`,
    background: disabled ? (isDark ? "#252526" : "#f0f0f0") : win.addr,
    color: disabled ? win.muted : win.text,
    cursor: disabled ? "default" : "pointer",
    fontSize: 14,
    opacity: disabled ? 0.5 : 1,
    fontFamily: "inherit",
  });

  return (
    <div style={{
      borderRadius: 8,
      border: `1px solid ${win.chromeBorder}`,
      overflow: "hidden",
      boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.35)" : "0 2px 10px rgba(0,0,0,0.08)",
      fontFamily: "'Segoe UI', 'DM Sans', system-ui, sans-serif",
    }}>
      {/* Barra superior estilo Explorador */}
      <div style={{
        padding: "8px 10px",
        background: win.chrome,
        borderBottom: `1px solid ${win.chromeBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
          <button type="button" title="Atrás" disabled={!canGoBack} onClick={goBack} style={btnStyle(!canGoBack)}>←</button>
          <button type="button" title="Subir un nivel" disabled={!canGoUp} onClick={goUp} style={btnStyle(!canGoUp)}>↑</button>
          <button type="button" title="Actualizar" disabled={loading} onClick={() => loadPath(currentPath)} style={btnStyle(loading)}>↻</button>

          {isRoot && (
            <div style={{ display: "flex", gap: 6, marginLeft: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={selectAllCaptaciones}
                style={{
                  padding: "4px 10px",
                  borderRadius: 4,
                  border: `1px solid ${win.chromeBorder}`,
                  background: win.addr,
                  color: win.text,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Seleccionar todas
              </button>
              <button
                type="button"
                onClick={deselectAllCaptaciones}
                style={{
                  padding: "4px 10px",
                  borderRadius: 4,
                  border: `1px solid ${win.chromeBorder}`,
                  background: win.addr,
                  color: win.text,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Quitar selección
              </button>
              <span style={{ fontSize: 12, color: win.muted, alignSelf: "center", marginLeft: 4 }}>
                {selectedCaptaciones.size} captación{selectedCaptaciones.size !== 1 ? "es" : ""} seleccionada{selectedCaptaciones.size !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>

        {/* Barra de dirección */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "6px 10px",
          borderRadius: 4,
          border: `1px solid ${win.chromeBorder}`,
          background: win.addr,
          fontSize: 13,
          flexWrap: "wrap",
          minHeight: 32,
        }}>
          <span style={{ color: win.muted, marginRight: 4, fontSize: 12 }}>📂 NAS</span>
          {crumbs.map((cr, i) => (
            <span key={cr.path} style={{ display: "inline-flex", alignItems: "center" }}>
              {i > 0 ? (
                <span style={{ color: win.muted, margin: "0 6px" }} aria-hidden="true">/</span>
              ) : null}
              <button
                type="button"
                onClick={() => navigateTo(cr.path)}
                style={{
                  background: "none",
                  border: "none",
                  padding: "2px 4px",
                  borderRadius: 3,
                  cursor: "pointer",
                  color: i === crumbs.length - 1 ? win.text : "#7c3aed",
                  fontWeight: i === crumbs.length - 1 ? 600 : 500,
                  fontSize: 13,
                  fontFamily: "inherit",
                  textDecoration: i === crumbs.length - 1 ? "none" : "underline",
                }}
              >
                {cr.label}
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Cabecera columnas */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isRoot ? "36px 1fr 140px 100px 88px" : "28px 1fr 140px 100px 88px",
        gap: 8,
        padding: "8px 12px",
        background: win.listHeader,
        borderBottom: `1px solid ${win.divider}`,
        fontSize: 12,
        fontWeight: 600,
        color: win.muted,
      }}>
        {isRoot && (
          <input
            type="checkbox"
            title="Seleccionar todas las captaciones"
            checked={allRootSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allRootSelected && someRootSelected;
            }}
            onChange={() => (allRootSelected ? deselectAllCaptaciones() : selectAllCaptaciones())}
            style={{ width: 16, height: 16, cursor: "pointer", justifySelf: "center" }}
          />
        )}
        {!isRoot && <span />}
        <span>Nombre</span>
        <span>Fecha modificación</span>
        <span>Tipo</span>
        <span style={{ textAlign: "right" }}>Tamaño</span>
      </div>

      {/* Lista */}
      <div style={{
        minHeight: 220,
        maxHeight: 420,
        overflowY: "auto",
        background: win.listBg,
      }}>
        {loading && (
          <div style={{ padding: 32, textAlign: "center", color: win.muted, fontSize: 13 }}>
            Leyendo NAS…
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: 20, color: "#f87171", fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: win.muted, fontSize: 13 }}>
            Carpeta vacía
          </div>
        )}

        {!loading && !error && items.map((item) => {
          const isDir = item.type === "directory";
          const childPath = currentPath ? `${currentPath}/${item.name}` : item.name;
          const isCaptacionRow = isRoot && isDir;
          const checked = isCaptacionRow && selectedCaptaciones.has(item.name);
          const focused = focusedName === item.name;

          return (
            <div
              key={item.name}
              role="row"
              tabIndex={0}
              onDoubleClick={() => {
                if (isDir) navigateTo(childPath);
              }}
              onClick={() => setFocusedName(item.name)}
              style={{
                display: "grid",
                gridTemplateColumns: isRoot ? "36px 1fr 140px 100px 88px" : "28px 1fr 140px 100px 88px",
                gap: 8,
                padding: "6px 12px",
                alignItems: "center",
                fontSize: 13,
                color: win.text,
                background: focused ? win.rowSelected : "transparent",
                borderBottom: `1px solid ${win.divider}`,
                cursor: isDir ? "pointer" : "default",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                if (!focused) e.currentTarget.style.background = win.rowHover;
              }}
              onMouseLeave={(e) => {
                if (!focused) e.currentTarget.style.background = "transparent";
              }}
            >
              {isRoot ? (
                isCaptacionRow ? (
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCaptacion(item.name)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, cursor: "pointer", justifySelf: "center" }}
                  />
                ) : (
                  <span />
                )
              ) : (
                <span />
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{fileIcon(item.name, isDir)}</span>
                {isDir ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); navigateTo(childPath); }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: win.text,
                      fontWeight: 500,
                      fontSize: 13,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.name}
                  </button>
                ) : (
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.name}
                  </span>
                )}
              </div>

              <span style={{ color: win.muted, fontSize: 12 }}>{formatDate(item.modified)}</span>
              <span style={{ color: win.muted, fontSize: 12 }}>{isDir ? "Carpeta" : "Archivo"}</span>
              <span style={{ color: win.muted, fontSize: 12, textAlign: "right" }}>{isDir ? "—" : formatBytes(item.size)}</span>
            </div>
          );
        })}
      </div>

      {/* Pie */}
      <div style={{
        padding: "8px 12px",
        background: win.listHeader,
        borderTop: `1px solid ${win.divider}`,
        fontSize: 12,
        color: win.muted,
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
      }}>
        <span>
          {items.length} elemento{items.length !== 1 ? "s" : ""}
          {isRoot && selectedCaptaciones.size > 0 && (
            <> · Marco editará: <strong style={{ color: win.text }}>{Array.from(selectedCaptaciones).join(", ")}</strong></>
          )}
        </span>
        <span style={{ fontSize: 11 }}>Doble clic en una carpeta para abrirla</span>
      </div>
    </div>
  );
}
